/**
 * Admin Audit Log Page
 *
 * Filterable list of admin audit log entries, grouped by date. Filters
 * (date, action type, admin email) are reflected in the URL query string so
 * a filtered view can be copied and shared. The action-type options come
 * from the event types seen on the current page rather than a hard-coded
 * list, since the audit log's event types are open-ended.
 */

import { useMemo } from "react";
import {
  Clock,
  ArrowsClockwise as RefreshCw,
  Shield,
  User,
} from "@phosphor-icons/react";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  StatusPill,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { useLoadMore } from "@/components/lists/useLoadMore";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminAuditLogs } from "@/hooks/useAdmin";
import type { AuditLogEntry } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const EVENT_TONES: Record<string, PillTone> = {
  user_update: "blue",
  user_disable: "red",
  user_enable: "green",
  password_reset: "amber",
};

const ROW_COLUMNS = "minmax(0,1fr) 110px 110px";

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** The label shown for an entry's action — see EVENT_TONES' own keys. */
function actionOf(entry: AuditLogEntry): string {
  return entry.action ?? entry.eventType;
}

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "timestamp", label: "Time" },
  { field: "action", label: "Action", kind: "text" },
];

/** Newest entries first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["date", "action", "actorEmail"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "timestamp", dir: "desc" },
};

const SORT_GETTERS: Record<
  string,
  (entry: AuditLogEntry) => string | undefined
> = {
  timestamp: (e) => e.timestamp,
  action: (e) => actionOf(e),
};

/** Whether an entry mentions `text` in any field an admin would look for. */
function matchesText(entry: AuditLogEntry, text: string): boolean {
  const haystack = [
    actionOf(entry),
    entry.targetType,
    entry.targetId,
    entry.adminEmail,
    entry.adminName,
    entry.userEmail,
    entry.userName,
    JSON.stringify(entry.details ?? {}),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(text.toLowerCase());
}

export function AdminAuditPage() {
  const list = useListUrlState(URL_CONFIG);
  const date = list.filters.date || todayISO();
  const filters = {
    date,
    action: list.filters.action!,
    actorEmail: list.filters.actorEmail!,
  };
  const pager = useLoadMore<AuditLogEntry>(
    JSON.stringify([date, filters.action, filters.actorEmail])
  );
  const hasActiveFilters = list.hasActive;

  const { data, isLoading, error, refetch, isFetching } = useAdminAuditLogs({
    date,
    action: filters.action || undefined,
    actorEmail: filters.actorEmail || undefined,
    cursor: pager.cursor,
    limit: 50,
  });

  const loadedEntries = useMemo(
    () => [...pager.previous, ...(data?.entries ?? [])],
    [pager.previous, data?.entries]
  );
  const displayEntries = useMemo(() => {
    const matching = list.search
      ? loadedEntries.filter((entry) => matchesText(entry, list.search))
      : loadedEntries;
    return sortRows(matching, list.sort, SORT_GETTERS);
  }, [loadedEntries, list.search, list.sort]);

  // Every distinct action/event type seen so far, for the action filter —
  // pulled from the data on hand rather than hard-coded, since the audit
  // log's event types are open-ended and can grow without this page's code
  // changing. Includes the currently-selected action even if the current
  // page doesn't have it, so the dropdown never silently drops the filter
  // that's actually applied.
  const actionOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const entry of loadedEntries) seen.add(actionOf(entry));
    if (filters.action) seen.add(filters.action);
    return [...seen].sort();
  }, [loadedEntries, filters.action]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Logs"
        description="Admin action history. All write operations are recorded."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      <AdminListToolbar
        label="Audit logs"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search action, target or details...",
          scope: "page",
        }}
        filters={[
          {
            id: "date",
            label: "Date",
            kind: "date",
            value: date,
            max: todayISO(),
            onChange: (v) => list.setFilter("date", v === todayISO() ? "" : v),
          },
          {
            id: "action",
            label: "Action",
            kind: "select",
            options: [
              { value: "", label: "All actions" },
              ...actionOptions.map((a) => ({ value: a, label: a })),
            ],
            value: filters.action,
            onChange: (v) => list.setFilter("action", v),
          },
          {
            id: "actorEmail",
            label: "Admin email",
            kind: "text",
            placeholder: "admin@example.com",
            value: filters.actorEmail,
            onChange: (v) => list.setFilter("actorEmail", v.trim()),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "page",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {/* Log entries */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load audit logs.")}
        </Alert>
      ) : (
        <>
          <DataTable>
            {displayEntries.length === 0 ? (
              <EmptyState
                icon={Shield}
                title={
                  hasActiveFilters
                    ? `No audit events match your filters on ${filters.date}.`
                    : `No audit events on ${filters.date}.`
                }
                description="Pick another date or clear the filters."
              />
            ) : (
              <>
                <DataTableHead columns={ROW_COLUMNS}>
                  <span>Event</span>
                  <span>Role</span>
                  <span>Time</span>
                </DataTableHead>
                <DataTableBody>
                  {displayEntries.map((entry, i) => (
                    <DataTableRow
                      key={`${entry.timestamp}-${i}`}
                      columns={ROW_COLUMNS}
                      className="items-start"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusPill
                              tone={EVENT_TONES[entry.eventType] ?? "neutral"}
                            >
                              {actionOf(entry)}
                            </StatusPill>
                            {entry.targetType && (
                              <span className="text-xs text-muted-foreground">
                                on {entry.targetType}
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            {entry.adminUserId && (
                              <span
                                className="inline-flex items-center gap-1"
                                title={entry.adminUserId}
                              >
                                <User className="h-3 w-3" />
                                Admin:{" "}
                                {entry.adminName ||
                                  entry.adminEmail ||
                                  `${entry.adminUserId.slice(0, 8)}…`}
                              </span>
                            )}
                            {entry.userId && (
                              <span
                                className="inline-flex items-center gap-1"
                                title={entry.userId}
                              >
                                <User className="h-3 w-3" />
                                Target:{" "}
                                {entry.userName ||
                                  entry.userEmail ||
                                  `${entry.userId.slice(0, 8)}…`}
                              </span>
                            )}
                          </div>
                          {Object.keys(entry.details ?? {}).length > 0 && (
                            <pre className="mt-2 max-w-lg overflow-x-auto rounded-lg bg-muted/50 p-2 text-xs">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {entry.adminRole}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatTime(entry.timestamp)}
                      </span>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </>
            )}
          </DataTable>

          {data?.cursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                pill
                onClick={() =>
                  pager.loadMore(data?.entries ?? [], data?.cursor)
                }
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
